import { faVideo } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Icon } from '@/components/ui/icon';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { cn } from '@/lib/utils';
import { buildYouTubeEmbedSrc, isYouTubeUrl } from '@/lib/youtube';
import { getVideoUrl, type VideoValue } from '@/types/video';
import { getDragThumbnailClassName } from '../editor/dnd/drag-visuals';
import { getEditorMediaFrameClassName } from '../editor/media-frame';

export type VideoBlockAspect = 'square' | 'portrait' | 'wide' | 'auto';

export interface VideoBlockProps {
  video?: VideoValue;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  aspect?: VideoBlockAspect;
}

export const VIDEO_BLOCK_ASPECT_VALUES: readonly VideoBlockAspect[] = [
  'square',
  'portrait',
  'wide',
  'auto',
];

const VIDEO_ASPECT_CLASSNAMES: Record<VideoBlockAspect, string> = {
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
  wide: 'aspect-video',
  auto: 'h-full min-h-[180px]',
};

export function VideoBlock({
  video,
  controls = true,
  autoplay = false,
  loop = false,
  muted = false,
  aspect = 'wide',
}: VideoBlockProps) {
  const isPageEditor = useIsPageEditor();
  const videoUrl = getVideoUrl(video);
  const dragThumbnailClassName = getDragThumbnailClassName({ isPageEditor });
  const editorMediaFrameClassName = getEditorMediaFrameClassName({
    isPageEditor,
    isEmpty: !videoUrl,
  });

  if (!videoUrl && !isPageEditor) return null;

  const aspectClassName = VIDEO_ASPECT_CLASSNAMES[aspect] || VIDEO_ASPECT_CLASSNAMES.wide;
  const isYouTube = videoUrl ? isYouTubeUrl(videoUrl) : false;

  if (isYouTube && videoUrl) {
    const embedSrc = buildYouTubeEmbedSrc(videoUrl, { autoplay, loop, muted, controls });
    if (!embedSrc) return null;

    return (
      <div
        className={cn('w-full overflow-hidden rounded-md', dragThumbnailClassName, aspectClassName)}
      >
        <iframe
          className="h-full w-full"
          src={embedSrc}
          title={video?.description || 'YouTube video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ border: 0, ...(isPageEditor ? { pointerEvents: 'none' } : {}) }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-md',
        editorMediaFrameClassName,
        dragThumbnailClassName,
        aspectClassName,
      )}
    >
      <video
        className={cn('h-full w-full object-cover', !videoUrl && 'invisible')}
        {...(videoUrl ? { src: videoUrl } : {})}
        aria-label={video?.description || 'Block video'}
        controls={Boolean(videoUrl) && controls}
        autoPlay={autoplay}
        loop={loop}
        muted={autoplay || muted}
        playsInline
        preload={videoUrl ? 'metadata' : 'none'}
        style={isPageEditor ? { pointerEvents: 'none' } : undefined}
      />
      {!videoUrl && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Icon icon={faVideo} className="size-3.5" />
            No video
          </span>
        </div>
      )}
    </div>
  );
}
